import React from 'react';

import { Typography } from '@mui/material';
import { Check } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

export interface ChecklistItem {
  readonly description: string;
  readonly title: string;
}

export interface ChecklistProps {
  readonly items: readonly ChecklistItem[];
  /** Replaces the default check mark next to every item. Ignored when `numbered`. */
  readonly icon?: React.ReactNode;
  /**
   * Steps to do in order, marked 1, 2, 3 instead of a check that reads as already done. A single
   * step gets no marker, since a lone "1" numbers nothing.
   */
  readonly numbered?: boolean;
}

const useStyles = makeStyles()(theme => ({
  check: {
    alignItems: 'center',
    backgroundColor: theme.appColors.selectedBackground,
    borderRadius: '50%',
    color: theme.appColors.primary,
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  // Nudged down so a 20px icon centers on the title's first line.
  icon: {
    display: 'flex',
    flexShrink: 0,
    marginTop: theme.spacing(0.25),
  },
  item: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  list: {
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    listStyle: 'none',
    margin: 0,
    padding: theme.spacing(2),
  },
  number: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: '50%',
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
}));

/** A boxed list of requirements or features, each a bold title over a secondary description. */
export const Checklist = React.memo(function Checklist({
  icon,
  items,
  numbered = false,
}: ChecklistProps): JSX.Element {
  const { classes } = useStyles();
  const itemIcon = icon ?? (
    <span className={classes.check}>
      <Check size={14} strokeWidth={3} />
    </span>
  );

  const List = numbered ? 'ol' : 'ul';
  const getMarker = (index: number) => {
    if (!numbered) return itemIcon;
    if (items.length === 1) return null;
    return <span className={classes.number}>{index + 1}</span>;
  };

  return (
    <List className={classes.list}>
      {items.map((item, index) => {
        const marker = getMarker(index);
        return (
          <li key={item.title} className={classes.item}>
            {marker && <span className={classes.icon}>{marker}</span>}
            <div>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{item.title}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                {item.description}
              </Typography>
            </div>
          </li>
        );
      })}
    </List>
  );
});
